// A player's token that isn't linked to their character (qa-runs 1250): its hot bar is empty and nothing else says why.
// Told to the DM and that token's owner alone, from what each was already sent (`unlinkedTokens`), with the link offered:
// the DM's through the route Properties uses, the owner's through their own. A token with one clear answer links itself
// (qa-runs 1251); this is for the rest, and for a table that was set up before that.
import { useState } from 'react';
import { unlinkedTokens } from '@dragons-table/vtt-shared';
import { linkCharacter, linkMyCharacter } from '../../characters';
import { useShell } from '../context';
import { useHolds } from '../../table/mayAct';
import { useTableState } from '../../table/TableState';
import { BTN } from '../ui';

export function UnlinkedNotice() {
  const { table, campaignId } = useShell();
  const { store, meId, scene, setNotice, viewingAs } = useTableState();
  const isDm = useHolds()('tokens') && !viewingAs;
  const [closed, setClosed] = useState<string[]>([]);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const rows = unlinkedTokens(store.state.objects, table.members, meId, isDm).filter((r) => !closed.includes(r.token));
  if (!rows.length) return null;
  const link = async (token: string, character: string) => {
    setBusy(token);
    const r = isDm ? await linkCharacter(campaignId, token, character, scene?.id ?? null) : await linkMyCharacter(campaignId, token, character);
    setBusy(null);
    if (r.kind === 'ok') { if (r.notice) setNotice(r.notice); return; }
    setNotice(r.kind === 'refused' ? r.reason : 'That could not be linked right now.');
  };
  return (
    <div className="flex shrink-0 flex-col gap-1 border-t border-amber-800 bg-stone-950 px-2 py-1 text-xs text-amber-200" role="status" data-testid="unlinked-tokens">
      {rows.map((r) => {
        const owner = table.members.find((m) => m.id === r.owner);
        const who = owner?.handle ? `@${owner.handle}` : 'its player';
        const pick = picks[r.token] ?? r.characters[0]!.id;
        return (
          <div key={r.token} className="flex flex-wrap items-center gap-1" data-testid={`unlinked-${r.token}`}>
            <span>{isDm ? `${r.name} is ${who}'s token, but isn't linked to their character.` : `Your token ${r.name} isn't linked to your character, so its hot bar can't read your sheet.`}</span>
            {r.characters.length > 1 && (
              <select aria-label={`Character for ${r.name}`} className="rounded bg-stone-800 px-1 py-0.5" value={pick} onChange={(e) => setPicks((was) => ({ ...was, [r.token]: e.target.value }))}>
                {r.characters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <button type="button" className={BTN} disabled={busy === r.token} data-testid="unlinked-link" onClick={() => void link(r.token, pick)}>
              Link{r.characters.length === 1 ? ` to ${r.characters[0]!.name}` : ''}
            </button>
            <button type="button" className={BTN} onClick={() => setClosed((was) => [...was, r.token])}>Not now</button>
          </div>
        );
      })}
    </div>
  );
}
